import { RocheUIGrid } from '@repo/roche-ui';
import { TranslationService } from '@rocheglobal/component-library-dot-com/src/services/translation';
import React, { useEffect, useRef, useState } from 'react';
import { ExternalDataService } from '../../services/external-data';
import { buildEndpointMap } from '../../services/external-data/build-endpoint-map';
import { rootDomain, storyblokRootDomain } from '../../services/external-data/types';
import { LanguageService } from '../../services/language';

interface ListProps {
  country: string;
}

interface ListResponse {
  canListen: boolean;
  Items: unknown[];
  Page: number;
  Total: number;
  headers: string[];
}

const getRow = (item: Record<string, unknown>) => [
  item['Country of the patient Organisation'],
  item.Division,
  item['Website of Patient Organisation'] && item['Website of Patient Organisation'] !== 'N/A'
    ? `
      <roche-generic-link
        target="_blank"
        url="${item['Website of Patient Organisation']}"
        content="${item['Receiving Patient Organisation']}"
      />`
    : item['Receiving Patient Organisation'],
  item['Type of Support'],
  item.Currency,
  item.Amount,
  `<div style="position: relative">
    <roche-tooltip icon="info" style="position:relative">${item['Project description']}</roche-tooltip>
  </div>`,
];

const Table = (props: ListProps): JSX.Element => {
  const { country } = props;
  const [state, setState] = useState({
    isFetching: false,
    items: [],
    total: 0,
    page: 1,
    canListen: false,
    headers: [],
  });
  const container = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const { current }: { current: HTMLElement } = container;
    if (!current) {
      return;
    }

    current.parentElement.addEventListener('rochePaginationPageChange', (event: CustomEvent) =>
      setState({ ...state, page: event.detail }),
    );
  }, [state.canListen]);

  useEffect(() => {
    setState((prevState) => ({ ...prevState, isFetching: true }));
    ExternalDataService.getPatients({ country, page: state.page })
      .then(async (response: ListResponse) => {
        const { Items, Page, Total } = response;

        setState({
          ...state,
          canListen: true,
          items: Items.map(getRow),
          page: Page,
          total: Total,
          headers: [
            await TranslationService.translate('country of the patient organisation'),
            await TranslationService.translate('division'),
            await TranslationService.translate('patient organisation'),
            await TranslationService.translate('type of support'),
            await TranslationService.translate('currency'),
            await TranslationService.translate('amount'),
          ],
        });
      })
      .finally(() => setState((prevState) => ({ ...prevState, isFetching: false })));
  }, [state.page]);

  if (state.isFetching) {
    return <roche-loading-spinner></roche-loading-spinner>;
  }

  if (!state.items.length) {
    return <roche-empty-state></roche-empty-state>;
  }

  return (
    <div ref={container}>
      <roche-responsive-table rows={JSON.stringify(state.items)} headers={JSON.stringify(state.headers)} />

      {state.total / itemsPerPage > 1 && (
        <roche-pagination
          total-pages={Math.ceil(state.total / itemsPerPage)}
          active-page={state.page}
        ></roche-pagination>
      )}
    </div>
  );
};

export const RochePatientOrganisations = ({ slot }: any): JSX.Element => {
  const [countries, setCountries] = useState([]);
  const [translations, setTranslations] = useState({
    selectCountryOfThePatientOrganisation: '',
    pleaseSelect: '',
  });
  const [currentCountry, setCurrentCountry] = useState('');
  const [canListen, setCanListen] = useState(false);
  const container = useRef(null);

  const endpoints = buildEndpointMap(rootDomain!, storyblokRootDomain!);

  const isRightToLeft = LanguageService.isRightToLeft();

  useEffect(() => {
    const { current } = container;
    if (!current) {
      return;
    }
    current.addEventListener('rocheDropdownChange', (event: CustomEvent) => {
      setCurrentCountry(event.detail.value.value);
    });
  }, [canListen]);

  useEffect(() => {
    ExternalDataService.get(endpoints.endpoint.patientCountries).then(async (response: string[]) => {
      setCountries(response);

      setTranslations({
        selectCountryOfThePatientOrganisation: await TranslationService.translate(
          'select country of the patient organisation',
        ),
        pleaseSelect: await TranslationService.translate('please select:'),
      });
      setCanListen(true);
    });
  }, []);

  if (countries.length <= 0) {
    return null;
  }

  const rocheDropdownItems = countries.map((country) => ({ label: country, value: country }));

  return (
    <div slot={slot} ref={container}>
      <RocheUIGrid layout='8-4' fullWidth={true} isRightToLeft={isRightToLeft}>
        <roche-dropdown
          label={translations?.selectCountryOfThePatientOrganisation}
          required
          slot={isRightToLeft ? 'right' : 'left'}
          options={JSON.stringify(rocheDropdownItems)}
          placeholder={translations?.pleaseSelect}
        />
      </RocheUIGrid>
      {countries.map((country, index) => (
        <div key={index}>{currentCountry === country && <Table country={country} />}</div>
      ))}
    </div>
  );
};
